import { CoreButtonView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { TWidgetCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { MouseEventHandler } from 'react';
import { getSemanticColors, SemanticColor } from '../../theme/colors';
import { TButtonOption } from '../../types/options';
import { isV2 } from '../../utils/widgetVersion';
import { VisualView } from '../Image/VisualView';
import { ButtonPillGlassView } from './ButtonPillGlassView';
import styles from './styles/buttonPill.module.css';
import {
    getHierarchyBgMap,
    getHierarchyTextMap,
    getMaxWidth,
    getPaddingPostfix,
    PADDING_MAP,
    PILL_FONT_STYLE_MAP,
    PILL_HEIGHT_MAP,
    resolvePillSize,
} from './styles/buttonPillUtils';

type TButtonPillStyleProps = Pick<TCoreButtonStyleProps, 'width' | 'height'>;

export type TButtonPillViewProps = {
    uid?: string;
    label?: string;
    dynamicShadow?: boolean;
    hierarchy?: TButtonOption['hierarchy'] | 'primaryBlack' | 'secondaryWhite' | 'tertiaryWhite';
    position?: 'floating' | 'inline';
    size?: 'large' | 'medium' | 'small';
    fillContainer?: boolean;
    maxWidth?: string;
    iconName?: string;
    flagIcon?: string;
    iconPosition?: 'left' | 'right';
    styleProps?: TButtonPillStyleProps;
    margin?: TButtonOption['margin'];
    /** Whether the document has been scrolled — controls dynamic shadow elevation. */
    documentScrolled?: boolean;
    onClick: MouseEventHandler<HTMLButtonElement>;
    /** Used solely for theme resolution via useThemeHook inside CoreButtonView. Optional. */
    widget?: TCoreButtonViewProps['widget'];
    parentStyleNames?: string[];
    /** Theme style key for the button container. Override to resolve a host-specific palette (e.g. `calculatorSubmitButton`). Defaults to `buttonPill`. */
    styleName?: string;
    /** Design version of the widget showing the button (`TWidget.version`): picks its semantic colors and icons. */
    version: number | undefined;
} & Pick<TWidgetCommonOption, 'isDisabled' | 'isHide'>;

/**
 * Composable view for the pill-shaped button used across consumer screens.
 * Owns only rendering logic — accepts all behavior props as pre-resolved inputs
 * from the parent (isDisabled, isHide, onClick, documentScrolled).
 *
 * Use this component when behavior state is already managed one level up,
 * e.g. inside a complex composed widget or a screen-level handler.
 * For server-driven usage, prefer ButtonPillWidget which wires up all hooks.
 */
export const ButtonPillView = ({
    uid,
    label,
    hierarchy = 'primary',
    iconName,
    flagIcon,
    iconPosition = 'left',
    size: sizeProp,
    position = 'inline',
    dynamicShadow,
    fillContainer,
    maxWidth,
    margin,
    documentScrolled,
    isDisabled,
    isHide,
    onClick,
    styleProps,
    widget,
    parentStyleNames: parentStyleNamesProp,
    styleName = 'buttonPill',
    version,
}: TButtonPillViewProps) => {
    const buttonSize = resolvePillSize(sizeProp);
    const getPosition = () => {
        const dynamicShadowDefault = dynamicShadow ?? true;
        if (dynamicShadowDefault && documentScrolled && !isDisabled) {
            return 'floating';
        } else {
            return position;
        }
    };

    const iconHierarchy = () => {
        switch (hierarchy) {
            case 'primary':
            case 'secondary':
                return 'emphasised';
            case 'tertiary':
                return 'secondary';
            default:
                return 'emphasised';
        }
    };

    if (hierarchy === 'tertiaryGlass') {
        return (
            <ButtonPillGlassView
                uid={uid}
                widget={widget}
                label={label}
                iconName={iconName}
                flagIcon={flagIcon}
                iconPosition={iconPosition}
                size={buttonSize}
                fillContainer={fillContainer}
                maxWidth={maxWidth}
                margin={margin}
                styleProps={styleProps}
                isDisabled={isDisabled}
                isHide={isHide}
                parentStyleNames={parentStyleNamesProp}
                styleName={styleName}
                onClick={onClick}
                version={version}
            />
        );
    }

    const semanticColors = getSemanticColors(version);
    const isV2Design = isV2({ version });
    // v2 Primary / Secondary have no stroke, even when floating.
    const hasFloatingStroke = getPosition() === 'floating' && buttonSize !== 'small' && !(isV2Design && (hierarchy === 'primary' || hierarchy === 'secondary'));
    // v2 Primary sits on a dark fill: its icon takes the inverted color.
    const iconColor =
        isV2Design && hierarchy === 'primary'
            ? semanticColors[isDisabled ? SemanticColor.IconPrimaryInvertedDisabled : SemanticColor.IconPrimaryInverted]
            : undefined;

    const paddingPostfix = getPaddingPostfix(!!(iconName || flagIcon), iconPosition, buttonSize);

    const coreButtonStyleProps: TCoreButtonStyleProps = {
        ...getHierarchyBgMap(semanticColors)[hierarchy],
        fillContainer: fillContainer ?? false,
        maxWidth: getMaxWidth(maxWidth),
        height: PILL_HEIGHT_MAP[buttonSize],
        ...styleProps,
        borderRadius: 76,
        padding: PADDING_MAP[paddingPostfix],
        fontStyle: PILL_FONT_STYLE_MAP[buttonSize],
        margin,
        borderStroke: hasFloatingStroke ? 4 : 0,
        boxShadow: getPosition() === 'floating' && !isDisabled ? 'var(--shadow-float-secondary)' : undefined,
        cursorDisabled: 'default',
    };

    const parentStyleNames = parentStyleNamesProp ?? [];

    const resolvedWidget: TCoreButtonViewProps['widget'] = widget ?? ({ uid: uid ?? '', data: { text: label }, options: {} } as TCoreButtonViewProps['widget']);

    return (
        <CoreButtonView
            widget={resolvedWidget}
            text={label}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={coreButtonStyleProps}
            parentStyleNames={parentStyleNames}
            styleName={styleName}
            onClick={onClick}
        >
            <ConditionalRender condition={!!(iconName || flagIcon)}>
                <div className={clsx(iconPosition === 'right' && styles.rightOrder)}>
                    <VisualView
                        version={version}
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="buttonPillIcon"
                        iconHierarchy={iconHierarchy()}
                        iconColor={iconColor}
                        iconVariant={'simple'}
                        size={buttonSize === 'small' ? 'xxSmall' : 'small'}
                        iconName={iconName}
                        flagIcon={flagIcon}
                    />
                </div>
            </ConditionalRender>
            <CoreTextView
                text={label}
                as="span"
                parentStyleNames={parentStyleNames}
                styleName={'buttonPillText'}
                isDisabled={isDisabled}
                styleProps={{ ...getHierarchyTextMap(semanticColors)[hierarchy] }}
            />
        </CoreButtonView>
    );
};
